<template>
  <content-block :class="$style.root">
    <div :class="$style.logo">
      <div :class="$style.icon">
        <logo />
      </div>
      <div :class="$style.text">
        {{ $t('brand.name') }}
      </div>
    </div>
    <form-item
      :title="$t('email')"
      vertical
    >
      <text-input
        v-model.trim="user.email"
        autocomplete="username"
      />
    </form-item>
    <form-item
      :title="$t('password')"
      vertical
    >
      <text-input
        v-model.trim="user.password"
        type="password"
        autocomplete="username-password"
      />
    </form-item>
    <btn
      all-width
      @click="login(user)"
    >
      {{ $t('action.enter') }}
    </btn>
    
    <div :class="$style.divider">
      <span>или</span>
    </div>
    
    <btn
      all-width
      variant="outline"
      :disabled="!googleLoaded || isGoogleLoading"
      @click="handleGoogleSignIn"
    >
      <svg
        style="margin-right: 8px; vertical-align: middle;"
        width="20"
        height="20"
        viewBox="0 0 24 24"
      >
        <path
          fill="#4285F4"
          d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
        />
        <path
          fill="#34A853"
          d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
        />
        <path
          fill="#FBBC05"
          d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
        />
        <path
          fill="#EA4335"
          d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
        />
      </svg>
      {{ getGoogleButtonText }}
    </btn>

    <btn
      all-width
      variant="outline"
      :disabled="!yandexLoaded || isYandexLoading"
      style="margin-top: 10px;"
      @click="handleYandexSignIn"
    >
      <img
        src="@/assets/yandex-logo.png"
        alt="Yandex"
        style="margin-right: 8px; vertical-align: middle; width: 20px; height: 20px; object-fit: contain;"
      >
      {{ getYandexButtonText }}
    </btn>
  </content-block>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import * as ACTIONS from "@/store/actions";
import * as GETTERS from "@/store/getters";
import Btn from '@/ui-kit/Btn';
import ContentBlock from '@/ui-kit/ContentBlock';
import FormItem from '@/ui-kit/FormItem';
import TextInput from '@/ui-kit/TextInput';
import Logo from '@/assets/logo/v2.svg';

export default {
  components: {
    Btn,
    ContentBlock,
    FormItem,
    TextInput,
    Logo,
  },
  data() {
    return {
      user: {
        email: process.env.USER || "", // user@domain.com
        password: process.env.PASSWORD || "", // string
      },
      googleLoaded: false,
      isGoogleLoading: false,
      yandexLoaded: false,
      isYandexLoading: false,
    };
  },
  computed: {
    ...mapGetters({
      loginError: GETTERS.AUTH_LOGIN_ERROR,
    }),
    error: {
      get() {
        return this.loginError;
      },
      set(val) {
        this.setLoginError(val);
      }
    },
    getGoogleButtonText() {
      if (!this.googleLoaded) {
        return 'Загрузка Google...';
      }
      if (this.isGoogleLoading) {
        return 'Вход...';
      }
      return 'Войти через Google';
    },
    getYandexButtonText() {
      if (!this.yandexLoaded) {
        return 'Загрузка Яндекс...';
      }
      if (this.isYandexLoading) {
        return 'Вход...';
      }
      return 'Войти через Яндекс';
    }
  },
  mounted() {
    // Инициализируем Google Sign-In после загрузки компонента
    this.$nextTick(() => {
      // Даём время Google SDK загрузиться
      setTimeout(() => {
        this.initializeGoogleSignIn();
        this.initializeYandexSignIn();
      }, 1000);
    });
  },
  methods: {
    ...mapActions({
      login: ACTIONS.AUTH_LOGIN,
      setLoginError: ACTIONS.AUTH_SET_LOGIN_ERROR,
      googleAuth: ACTIONS.AUTH_GOOGLE,
      yandexAuth: ACTIONS.AUTH_YANDEX,
    }),
    initializeGoogleSignIn() {
      // Проверяем, загружен ли Google SDK
      if (!window.google) {
        console.error('Google SDK не загружен');
        this.setLoginError('Google SDK не загружен. Проверьте подключение к интернету.');
        return;
      }

      try {
        // Инициализируем Google Sign-In БЕЗ FedCM - используем только popup
        window.google.accounts.id.initialize({
          client_id: '687586322584-algoj1fs5da4o47kbr9hm41v0k264cfb.apps.googleusercontent.com',
          callback: async (response) => {
            try {
              console.log('Google token received:', response.credential);
              // Отправляем ID token на бэкенд (как в обычной авторизации)
              await this.googleAuth({ google_token: response.credential });
            } catch (error) {
              console.error('Google auth error:', error);
              this.setLoginError('Ошибка аутентификации через Google');
            } finally {
              this.isGoogleLoading = false;
            }
          },
          // Отключаем FedCM и One Tap
          use_fedcm_for_prompt: false,
          auto_select: false
        });
        
        this.googleLoaded = true;
      } catch (error) {
        console.error('Error initializing Google Sign-In:', error);
        this.setLoginError('Ошибка инициализации Google Sign-In');
      }
    },

    handleGoogleSignIn() {
      if (!this.googleLoaded || this.isGoogleLoading) {
        return;
      }

      this.isGoogleLoading = true;
      
      try {
        // Создаем временный div для кнопки Google
        const tempDiv = document.createElement('div');
        tempDiv.style.position = 'fixed';
        tempDiv.style.top = '-1000px'; // Скрываем за экраном
        document.body.appendChild(tempDiv);
        
        // Рендерим кнопку Google (это обходит FedCM и использует popup)
        window.google.accounts.id.renderButton(tempDiv, {
          theme: 'outline',
          size: 'large',
          type: 'standard',
          text: 'signin_with'
        });
        
        // Программно кликаем по кнопке
        setTimeout(() => {
          const googleButton = tempDiv.querySelector('div[role="button"]');
          if (googleButton) {
            googleButton.click();
          } else {
            this.setLoginError('Не удалось запустить Google Sign-In');
            this.isGoogleLoading = false;
          }
          
          // Убираем временный div
          setTimeout(() => {
            if (document.body.contains(tempDiv)) {
              document.body.removeChild(tempDiv);
            }
          }, 1000);
        }, 100);
        
      } catch (error) {
        console.error('Error triggering Google Sign-In:', error);
        this.setLoginError('Ошибка запуска Google Sign-In');
        this.isGoogleLoading = false;
      }
    },

    initializeYandexSignIn() {
      // Для popup авторизации Yandex SDK не нужен
      console.log('Yandex popup авторизация готова');
      this.yandexLoaded = true;
    },

    handleYandexSignIn() {
      if (!this.yandexLoaded || this.isYandexLoading) {
        return;
      }

      this.isYandexLoading = true;
      
      try {
        // Создаём URL для Yandex OAuth
        const clientId = '7264fbde23dc40acaa2844de74b18836';
        const redirectUri = encodeURIComponent(window.location.origin + '/yandex-callback.html');
        const state = Math.random().toString(36).substring(2, 15);
        
        // Запрашиваем только базовые данные (ID и email)
        const authUrl = `https://oauth.yandex.ru/authorize?response_type=code&client_id=${clientId}&redirect_uri=${redirectUri}&state=${state}&force_confirm=yes`;
        
        // Открываем popup окно
        const popup = window.open(
          authUrl,
          'yandex-auth',
          'width=500,height=600,scrollbars=yes,resizable=yes'
        );

        // Слушаем сообщения от popup
        const messageListener = (event) => {
          if (event.origin !== window.location.origin) {
            return;
          }
          
          if (event.data.type === 'YANDEX_AUTH_SUCCESS') {
            console.log('Yandex code received:', event.data.code);
            
            // Отправляем код на бэкенд для обмена на токен
            this.yandexAuth({ yandex_code: event.data.code })
              .finally(() => {
                this.isYandexLoading = false;
              });
            
            popup.close();
            window.removeEventListener('message', messageListener);
          } else if (event.data.type === 'YANDEX_AUTH_ERROR') {
            console.error('Yandex auth error:', event.data.error);
            this.setLoginError('Ошибка авторизации через Яндекс');
            this.isYandexLoading = false;
            popup.close();
            window.removeEventListener('message', messageListener);
          }
        };

        window.addEventListener('message', messageListener);
        
        // Проверяем, не закрыл ли пользователь popup
        const checkClosed = setInterval(() => {
          if (popup.closed) {
            clearInterval(checkClosed);
            window.removeEventListener('message', messageListener);
            this.isYandexLoading = false;
          }
        }, 1000);

      } catch (error) {
        console.error('Error opening Yandex popup:', error);
        this.setLoginError('Ошибка запуска Yandex авторизации');
        this.isYandexLoading = false;
      }
    },
  },
};
</script>

<style lang="scss" module>
@import '~@/ui-kit/_theme/main.scss';

.root {
  width: 350px;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -55%);
}

.logo {
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  top: -82px;
  margin-left: 24px;

  .icon {
    width: 70px;
    margin-right: -2px;
    transform: translateY(-3px);
  }

  .text {
    color: $text-primary-color;
    opacity: 0.7;
    font-weight: 400;
    font-size: 19px;
    white-space: nowrap;
    letter-spacing: 0.085em;
    margin-bottom: 16px;
  }
}

.divider {
  display: flex;
  align-items: center;
  margin: 20px 0;
  
  &::before,
  &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: #e0e0e0;
  }
  
  span {
    padding: 0 15px;
    color: #999;
    font-size: 14px;
  }
}


.bg {
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50.5%, -50%);
}
</style>
